import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { routesHandler } from 'Checkout/Routes/routesHandler'
import { useCurrentReservation } from 'Checkout/Hooks/useCurrentReservation'
import { useDebouncedCallback } from 'use-debounce'
import { getPricing } from 'Checkout/Redux/Slices/CartSlice'
import { cloneWithoutKeys } from 'Utils/objects'

const RESERVATION_CART_KEYS_TO_IGNORE = ['customFields', 'data', 'dayUse', 'specialRequests', 'vehicle']

export const useReservationsPricing = () => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const { couponCode, discount, isNonRefundable, isPurchaseComplete,
          isTripProtection } = useSelector(state => state.payment)
  const email = useSelector(state => state.details.email)
  const editingReservationId = useSelector(state => state.cart.editingReservationId)
  const reservations = useSelector(state => state.cart.reservations)
  const { canUseSiteLock, isAutoAssign, isRequest } = useSelector(state => state.campground)
  const currentReservation = useCurrentReservation()

  const fetchPricingDebounced = useDebouncedCallback(reservationsToApi => {
    const variables = { reservationsData: reservationsToApi, slug: window.roverData.campgroundSlug }
    dispatch(getPricing({ variables }))
  }, 350)

  const buildSite = ({ siteId, isSiteLock }) => {
    if (isRequest || (canUseSiteLock && !isSiteLock) || (isAutoAssign && !isSiteLock)) return null

    return siteId
  }

  const buildReservationsFromCart = () => {
    const reservationsFromCart = reservations.map(originalReservation => {
      if (originalReservation.nanoId === editingReservationId && currentReservation.rateId) {
        const { customFields: _, data: __, dayUse: ___, ...rest } = currentReservation
        return {
          ...rest,
          extras: {
            ...rest.extras,
            siteId: buildSite(rest.extras),
          },
        }
      }

      const reservation = cloneWithoutKeys(originalReservation, RESERVATION_CART_KEYS_TO_IGNORE)

      return {
        ...reservation,
        inProgress: reservation.nanoId === editingReservationId || !reservation.nanoId,
        extras: {
          ...reservation.extras,
          couponCode,
          discountClubId: discount?.id,
          email,
          nonRefundable: isNonRefundable,
          hasTripInsurance: isTripProtection,
          siteId: buildSite(reservation.extras),
        },
      }
    })

    return reservationsFromCart
  }

  const fetchPrice = () => {
    const isReservationsEmpty = (!currentReservation.rateId && !reservations.length)
    const isPricingNotNeeded = isPurchaseComplete || isReservationsEmpty
    if (isPricingNotNeeded) return

    const reservationsToApi = buildReservationsFromCart()
    const skipCurrent = [routesHandler.payment.path, routesHandler.cart.path].includes(pathname)

    if (currentReservation.rateId && !editingReservationId && !skipCurrent) {
      const { customFields: _, data: __, dayUse: ___, ...rest } = currentReservation
      reservationsToApi.push(rest)
    }

    fetchPricingDebounced(reservationsToApi)
  }

  return fetchPrice
}
